/* eslint-disable max-len */
import { DownloadOutlined } from '@ant-design/icons';
import { Form, message } from 'antd';
import { Formik } from 'formik';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type ApplicationListEndpointContextDataFragment } from '../../../../api/fragments/ApplicationListEndpointContextData';
import { type ModuleListDataFragment } from '../../../../api/fragments/ModuleListData';
import { useListModulesForApplicationDownloadQuery } from '../../../../api/queries';
import { useAccountContext } from '../../../../components/contexts/AccountContextManager';
import { useCompany } from '../../../../components/contexts/CompanyContextManager';
import { useSingleDealerId } from '../../../../components/contexts/DealerContextManager';
import { useLanguage } from '../../../../components/contexts/LanguageContextManager';
import DownloadModal, { type DownloadState } from '../../../../components/fields/DownloadModal/DownloadModal';
import { usePasswordModal } from '../../../../components/fields/DownloadModal/PasswordModal';
import useDownloadOptions from '../../../../components/fields/DownloadModal/useDownloadOptions';
import LoadingElement from '../../../../components/LoadingElement';
import BasicProPageWithHeader from '../../../../layouts/BasicProLayout/BasicProPageWithHeader';
import { useThemeComponents } from '../../../../themes';
import { ExportFormat, exportApplications } from '../../../../utilities/export';
import ApplicationList, { ApplicationColumns } from '../../../shared/ApplicationList';
import { getApplicationColumnsForCI } from '../../../shared/ApplicationList/helpers';

export type ApplicationListPageProps = {
    endpoint: ApplicationListEndpointContextDataFragment;
    namespace: string;
};

const allowedCapFormatTypes = new Set<ModuleListDataFragment['__typename']>([
    'FinderApplicationPublicModule',
    'FinderApplicationPrivateModule',
    'EventApplicationModule',
    'ConfiguratorModule',
]);

const allOption = { label: 'All', value: 'all' };

const initialValues: DownloadState = {
    module: null,
    period: { start: undefined, end: undefined },
    format: ExportFormat.DefaultFormat,
};

const ApplicationListPageInner = ({ endpoint, namespace }: ApplicationListPageProps) => {
    const { t } = useTranslation(namespace);
    const stage = endpoint.applicationStage;
    const [visible, setVisible] = useState(false);
    const [moduleListByApplication, setModuleListByApplication] = useState<string[]>([]);
    const { Button } = useThemeComponents();
    const { dealerId } = useSingleDealerId();

    const [formatVisible, setFormatVisible] = useState(false);
    const [downloading, setDownloading] = useState(false);

    const company = useCompany(true);
    const passwordModal = usePasswordModal();
    const { token } = useAccountContext();
    const { formatOptions } = useDownloadOptions();
    const language = useLanguage();

    const setDefault = useCallback(() => {
        setVisible(false);
        setFormatVisible(false);
        setDownloading(false);
    }, [setVisible, setFormatVisible, setDownloading]);

    const { data, loading } = useListModulesForApplicationDownloadQuery({
        fetchPolicy: 'cache-and-network',
        variables: { filter: { companyIds: company ? [company.id] : [], stage } },
    });

    const modules = data?.modules;

    const channelModuleOption = useMemo(() => {
        const options = (modules || [])
            .filter(
                module =>
                    moduleListByApplication.includes(module.id) &&
                    (module.__typename === 'StandardApplicationModule' ||
                        module.__typename === 'EventApplicationModule' ||
                        module.__typename === 'ConfiguratorModule' ||
                        module.__typename === 'MobilityModule' ||
                        module.__typename === 'FinderApplicationPublicModule' ||
                        module.__typename === 'FinderApplicationPrivateModule' ||
                        module.__typename === 'SdmModule' ||
                        module.__typename === 'AudiFinancingModule' ||
                        module.__typename === 'BmwModule')
            )
            .map(module => ({ label: module.displayName, value: module.id }));

        return [...(company && options.length > 1 ? [allOption] : []), ...options];
    }, [company, moduleListByApplication, modules]);

    const isAllCapModules = useMemo(
        () => (modules || []).every(module => allowedCapFormatTypes.has(module.__typename)),
        [modules]
    );

    const onChannelChange = useCallback(
        (value: string) => {
            const foundModule = modules.find(module => module.id === value);
            if (allowedCapFormatTypes.has(foundModule?.__typename) || (value === allOption.value && isAllCapModules)) {
                setFormatVisible(true);
            } else {
                setFormatVisible(false);
            }
        },
        [isAllCapModules, modules]
    );

    useEffect(() => {
        if (channelModuleOption?.length === 1) {
            const selectedModuleId = channelModuleOption[0].value;

            const foundModule = modules.find(module => module.id === selectedModuleId);
            if (foundModule && allowedCapFormatTypes.has(foundModule.__typename)) {
                setFormatVisible(true);
            }
        }
    }, [modules, channelModuleOption, visible]);

    const onSubmit = useCallback(
        async (values, actions) => {
            if (!values.module) {
                return;
            }
            try {
                setDownloading(true);

                const dealerIds = [dealerId];
                const moduleIds =
                    values.module === allOption.value
                        ? channelModuleOption
                              .filter(option => option.value !== allOption.value)
                              .map(({ value }) => value)
                        : [values.module];

                // all modules are on the same company
                const selectedModule = modules.find(data => data.id === moduleIds[0]);

                const password = await exportApplications({
                    dealerIds,
                    stage,
                    moduleIds,
                    period: values.period,
                    companyName: selectedModule.company.displayName,
                    token,
                    format: values.format,
                    languageId: language?.currentLanguageId || null,
                });

                if (password) {
                    passwordModal.open(password);
                }
            } catch (error) {
                message.error(t('ciModal:messages.noRecordToDownload'));
            } finally {
                // Pre-select module if there is no other choice
                if (channelModuleOption.length !== 1) {
                    actions.resetForm();
                } else {
                    actions.resetForm({
                        values: {
                            module: values.module,
                            period: initialValues.period,
                            format: initialValues.format,
                        },
                    });
                }
                setDefault();
            }
        },
        [channelModuleOption, dealerId, modules, setDefault, passwordModal, stage, t, token]
    );

    const showColumns = useMemo(() => {
        const columnList = getApplicationColumnsForCI(stage);

        if (!company) {
            columnList.unshift(ApplicationColumns.Company);
        }

        return columnList;
    }, [company, stage]);

    const openDownloadModal = useCallback(() => {
        setVisible(true);
    }, [setVisible]);

    const extra = (
        <Button icon={<DownloadOutlined />} onClick={openDownloadModal} type="primary">
            {t('actions.download')}
        </Button>
    );

    if (loading) {
        return <LoadingElement />;
    }

    return (
        <BasicProPageWithHeader
            extra={extra}
            style={{ height: '100%', backgroundColor: '#fff' }}
            title={endpoint.title}
        >
            <Formik initialValues={initialValues} onSubmit={onSubmit}>
                {({ handleSubmit }) => (
                    <Form id="downloadModalForm" name="downloadModalForm" onSubmitCapture={handleSubmit}>
                        <DownloadModal
                            channelModuleOption={channelModuleOption}
                            downloading={downloading}
                            formatOptions={formatOptions}
                            formatVisible={formatVisible}
                            namespace={namespace}
                            onChannelChange={onChannelChange}
                            passwordModal={passwordModal}
                            setDefault={setDefault}
                            visible={visible}
                            forAdmin
                        />
                    </Form>
                )}
            </Formik>
            <ApplicationList
                dealerIds={[dealerId]}
                moduleIds={endpoint.applicationModuleIds}
                setModuleListByApplication={setModuleListByApplication}
                showColumns={showColumns}
                stage={endpoint.applicationStage}
            />
        </BasicProPageWithHeader>
    );
};

export default ApplicationListPageInner;
